<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useOnline, usePreferredReducedMotion } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import type {
    TMenuImportEntry,
    TMenuSidePanelTab,
  } from '@/composition/menu-import/menu-import-context';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import { useLiveUpdateView } from '@/composition/menu-import/use-live-update-view';
  import {
    EMPTY_FLOW_BAR,
    flowBar,
  } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import { useGtag } from '@/composition/use-gtag';
  import { useModal } from '@/composition/use-modal';
  import type { IListReveal } from '@/domain/menu-import/assistant-view';
  import { proposalPreview } from '@/domain/menu-import/draft-list';
  import {
    draftSummary,
    filterCounts,
    matchesItemFilter,
  } from '@/domain/menu-import/menu-draft';
  import type { TItemFilter } from '@/domain/menu-import/menu-draft';
  import { publishSource } from '@/domain/menu-import/publish-dialog';
  import type { TPublishSource } from '@/domain/menu-import/publish-dialog';
  import {
    firstFlaggedItemId,
    hasSomethingToReview,
    isReadFinish,
    isReadIncomplete,
    openQuestions,
    publishBlockerTarget,
    readCopyKeys,
    readDoneText,
    readFoundCount,
    readSlowDelay,
    retakePages,
    sidePanelUseful,
    workspaceBarModel,
    workspacePhase,
  } from '@/domain/menu-import/workspace-view';
  import type { IBarText } from '@/domain/menu-import/workspace-view';
  import type {
    IAnswerQuestionDto,
    TDraftOp,
    TDraftQuestion,
  } from '@/interfaces/IMenuImport';
  import V2MenuSidePanel from '../assistant/V2MenuSidePanel.vue';
  import V2MenuDraftList from './V2MenuDraftList.vue';
  import V2MenuFilterBar from './V2MenuFilterBar.vue';
  import V2MenuPageFailedCard from './V2MenuPageFailedCard.vue';
  import V2MenuQuestionCard from './V2MenuQuestionCard.vue';
  import V2MenuReadFailed from './V2MenuReadFailed.vue';
  import V2MenuReadingSide from './V2MenuReadingSide.vue';
  import V2MenuReadProgress from './V2MenuReadProgress.vue';
  import V2MenuReviewQueue from './V2MenuReviewQueue.vue';
  import V2MenuSummary from './V2MenuSummary.vue';

  /**
   * The menu workspace (plan §4.4 reading, §4.5 review): the menu column
   * (progress or the one-time «Terminamos de leer», the summary, filters,
   * questions, the list building itself, failed pages) and, on wide screens,
   * a 380 px right pane: «Mientras leemos» during a read, the side panel
   * («Pedir cambios» | «Carta original») in the review. On narrow screens the
   * side panel lives in a bottom sheet opened by a floating «Pedir cambios».
   * The side panel host is one node (`PosUiSheet`, docked on wide), never a
   * tier-dependent `v-if`, so a tier change never remounts the chat composer.
   * The latest read failing with nothing built shows the failed state. An
   * update (a store with dishes, plan §3.5) is wired by `useLiveUpdateView`.
   * Full-bleed panes (the workspace archetype), justified by density.
   */
  const MAX_VISIBLE_QUESTIONS = 3;

  const props = defineProps<{
    entry: TMenuImportEntry;
    wide: boolean;
    phone: boolean;
    /** The store's country (price formats). */
    country: string;
  }>();
  const emit = defineEmits<{
    /** To the source screen; `paste` opens its «Escriba o pegue su menú» card. */
    (e: 'source', paste: boolean): void;
    /** «Seguir después». */
    (e: 'later'): void;
    /** «Publicar menú» with nothing blocking it: the flow opens the publish dialog. */
    (e: 'publish'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));
  const ctx = useMenuImport();
  const modals = useModal();
  const gtag = useGtag();
  const online = useOnline();
  const reducedMotion = usePreferredReducedMotion();
  const actions = useDraftActions({
    openAssistant,
    country: () => props.country,
  });

  const main = ref<HTMLElement | null>(null);
  const list = ref<InstanceType<typeof V2MenuDraftList> | null>(null);
  /** A retry, the manual start or a cancel is running. */
  const acting = ref(false);
  /** The questions whose answers are being saved (only their cards wait). */
  const answeringIds = ref<ReadonlySet<string>>(new Set());
  const showAllQuestions = ref(false);
  /** Failed pages the owner chose to go on without («Seguir sin ella»). */
  const dismissedPages = ref<ReadonlySet<string>>(new Set());
  /** The read whose end this screen saw: the one-time «Terminamos de leer» card. */
  const doneReadNo = ref<number | null>(null);
  const slow = ref(false);
  /** A category just added: its name opens for typing. */
  const renameCategoryId = ref<string | null>(null);
  let slowTimer: ReturnType<typeof setTimeout> | null = null;
  let alive = true;

  //#region State
  const draft = computed(() => ctx.draft.value);
  const read = computed(() => ctx.read.value);
  const pages = computed(() => ctx.pages.value);
  const aiEnabled = computed(() => ctx.aiEnabled.value);
  const filter = computed(() => ctx.filter.value);
  const listMode = computed(() => ctx.mode.value === 'list');
  const openItemId = computed(() => ctx.openItemId.value);
  const selectedItemId = computed(() => ctx.selectedItemId.value);
  const sheetOpen = computed(() => ctx.sheetOpen.value);
  const sheetHeight = computed(() => ctx.sheetHeight.value);
  const sidePanelTab = computed(() => ctx.sidePanelTab.value);
  const currency = computed(() => ctx.displayCurrency.value);

  const phase = computed(() =>
    workspacePhase(read.value, draft.value, ctx.manual.value)
  );
  const reading = computed(() => phase.value === 'reading');
  const summary = computed(() =>
    draft.value ? draftSummary(draft.value) : null
  );
  const hasItems = computed(() => (summary.value?.items ?? 0) > 0);
  /** AI-read, copied or hand-built: «Encontramos…» only for the first. */
  const source = computed<TPublishSource>(() =>
    draft.value
      ? publishSource(ctx.snapshotMeta.value?.kind ?? 'read', draft.value)
      : 'read'
  );
  const counts = computed(() =>
    draft.value ? filterCounts(draft.value) : null
  );
  const {
    filters,
    barUpdate,
    upToDate,
    summaryUpdate,
    newMenuOpen,
    readingUpdate,
    othersExpanded,
    showOthers,
  } = useLiveUpdateView({
    draft: () => draft.value,
    summary: () => summary.value,
    counts: () => counts.value,
    scrollToOthers: () => list.value?.scrollToOthers(),
  });
  const questions = computed(() =>
    draft.value && phase.value === 'review' ? openQuestions(draft.value) : []
  );
  const visibleQuestions = computed(() =>
    showAllQuestions.value
      ? questions.value
      : questions.value.slice(0, MAX_VISIBLE_QUESTIONS)
  );
  const moreQuestions = computed(
    () => questions.value.length - visibleQuestions.value.length
  );
  /** The filter chips are hidden while reading: the list shows everything. */
  const listFilter = computed<TItemFilter>(() =>
    reading.value ? 'all' : filter.value
  );
  const preview = computed(() => proposalPreview(ctx.turns.value));
  /** While reading, the list appears with the first category (skeleton rows until then). */
  const showList = computed(
    () =>
      draft.value !== null &&
      (!reading.value || draft.value.categoryOrder.length > 0)
  );
  const found = computed(() =>
    read.value ? readFoundCount(read.value, draft.value) : 0
  );
  const retakes = computed(() =>
    hasItems.value
      ? retakePages(read.value, pages.value).filter(
          (entry) => !entry.pageIds.every((id) => dismissedPages.value.has(id))
        )
      : []
  );
  const partialCount = computed(() =>
    read.value?.status === 'partial' && hasItems.value && !reading.value
      ? read.value.itemsFound
      : null
  );
  /**
   * The read left something out (partial, or a failed or too dense page):
   * the done card and the summary never say «Todo listo» then (one rule).
   */
  const readIncomplete = computed(() =>
    isReadIncomplete(read.value, pages.value)
  );
  const doneText = computed(() => {
    const current = summary.value;
    if (!current) return null;
    return readDoneText({
      toReview: current.toCheck,
      // «Parece un menú nuevo» waits for its answer like a blocking question.
      openBlockingQuestions:
        current.openBlockingQuestions + (newMenuOpen.value ? 1 : 0),
      incomplete: readIncomplete.value,
      upToDate: upToDate.value,
    });
  });
  const showDoneCard = computed(
    () =>
      phase.value === 'review' &&
      read.value !== null &&
      doneReadNo.value === read.value.readNo &&
      doneText.value !== null
  );
  const doneLine = computed(() => barText(doneText.value) ?? '');
  /** «Revisar mi menú» only when it has a dish or a question to go to. */
  const canReviewMenu = computed(() =>
    draft.value ? hasSomethingToReview(draft.value) : false
  );
  /** With the AI off and no menu photo, the side panel would only say the chat is off. */
  const hasSidePanel = computed(() =>
    sidePanelUseful(aiEnabled.value, pages.value)
  );
  const showFab = computed(
    () =>
      !props.wide &&
      phase.value === 'review' &&
      !sheetOpen.value &&
      hasSidePanel.value
  );
  const sheetTitle = computed(() =>
    sidePanelTab.value === 'original'
      ? tr('menuImport.original')
      : tr('menuImport.chat.title')
  );
  //#endregion

  //#region Bottom bar
  function barText(text: IBarText | null): string | null {
    if (text === null) return null;
    return text.count === null
      ? tr(text.key)
      : trc(text.key, text.count, { count: text.count });
  }

  const bar = computed<IFlowBarState>(() => {
    const current = summary.value;
    if (!current) return EMPTY_FLOW_BAR;
    const model = workspaceBarModel({
      phase: phase.value,
      wide: props.wide,
      online: online.value,
      aiEnabled: aiEnabled.value,
      summary: current,
      update: barUpdate.value,
    });
    return flowBar({
      leftLabel: barText(model.left),
      helper: barText(model.helper),
      helperIsLink: model.helperIsLink,
      secondaryLabel: barText(model.secondary),
      primaryLabel: barText(model.primary),
      primaryDisabled: model.primaryDisabled,
      primaryAriaDisabled: model.primaryAriaDisabled,
      primaryBusy: model.primaryBusy,
    });
  });

  /** «Publicar menú»; while something blocks it, opens the first thing to fix. */
  async function primary() {
    const state = bar.value;
    if (
      state.primaryLabel === null ||
      state.primaryDisabled ||
      state.primaryBusy
    )
      return;
    if (state.primaryAriaDisabled) {
      await revealBlocker();
      return;
    }
    emit('publish');
  }

  /** «Seguir después». */
  async function secondary() {
    if (bar.value.secondaryLabel !== null) emit('later');
  }

  /** «Cargar otra vez». */
  async function back() {
    if (bar.value.leftLabel !== null) readAgain();
  }
  //#endregion

  //#region Scrolling to things
  function blurActive() {
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
  }

  /** The element of the list carrying `attribute="value"` (ids never need escaping this way). */
  function findMarked(attribute: string, value: string): HTMLElement | null {
    const root = main.value;
    if (!root) return null;
    const marked = Array.from(root.querySelectorAll(`[${attribute}]`)).find(
      (element) => element.getAttribute(attribute) === value
    );
    return marked instanceof HTMLElement ? marked : null;
  }

  /**
   * Inside the list pane and clear of the floating «Pedir cambios» (the
   * pane's `scroll-padding-bottom` while it shows).
   */
  function isInView(element: HTMLElement): boolean {
    const root = main.value;
    if (!root) return false;
    const pane = root.getBoundingClientRect();
    const box = element.getBoundingClientRect();
    const covered = parseFloat(getComputedStyle(root).scrollPaddingBottom) || 0;
    return box.top >= pane.top && box.bottom <= pane.bottom - covered;
  }

  function scrollTo(element: HTMLElement) {
    element.scrollIntoView({
      block: 'center',
      behavior: reducedMotion.value === 'reduce' ? 'auto' : 'smooth',
    });
  }

  /** Shows a dish: the list, a filter that includes it, then scrolled into view when off screen. */
  async function revealItem(itemId: string) {
    const item = draft.value?.items[itemId];
    if (!item) return;
    if (!listMode.value) ctx.mode.value = 'list';
    if (!reading.value && !matchesItemFilter(item, filter.value))
      ctx.filter.value = 'all';
    await nextTick();
    if (!alive) return;
    const row = findMarked('data-item-id', itemId);
    if (row && !isInView(row)) scrollTo(row);
  }

  async function revealQuestion(questionId: string) {
    if (!listMode.value) ctx.mode.value = 'list';
    if (!visibleQuestions.value.some((open) => open.id === questionId))
      showAllQuestions.value = true;
    await nextTick();
    if (!alive) return;
    const card = findMarked('data-question-id', questionId);
    if (!card) return;
    scrollTo(card);
    const firstButton = card.querySelector('button:not([disabled])');
    if (firstButton instanceof HTMLElement)
      firstButton.focus({ preventScroll: true });
  }

  /**
   * Narrow screens, the side panel's sheet just went down to 40 % («Ver
   * cambios», a box tapped in «Carta original»): the row, else its
   * category, at the top of the list, clear of the sheet.
   */
  async function revealRow(target: IListReveal) {
    if (!listMode.value) ctx.mode.value = 'list';
    await nextTick();
    if (!alive) return;
    const row =
      (target.itemId !== null
        ? findMarked('data-item-id', target.itemId)
        : null) ??
      (target.categoryId !== null
        ? findMarked('data-category-id', target.categoryId)
        : null);
    row?.scrollIntoView({
      block: 'start',
      behavior: reducedMotion.value === 'reduce' ? 'auto' : 'smooth',
    });
  }

  /** The blocked «Publicar menú»: the first dish missing a price (opened), or the first blocking question. */
  async function revealBlocker() {
    const current = draft.value;
    const target = current ? publishBlockerTarget(current) : null;
    if (!target) return;
    if (target.kind === 'question') {
      await revealQuestion(target.id);
      return;
    }
    if (openItemId.value === target.id) await revealItem(target.id);
    else ctx.openItemId.value = target.id;
  }

  /** «Revisar mi menú»: the first thing to review. */
  async function reviewMenu() {
    const current = draft.value;
    if (!current) return;
    const itemId = firstFlaggedItemId(current);
    if (itemId !== null) {
      await revealItem(itemId);
      return;
    }
    const question = questions.value[0];
    if (question) await revealQuestion(question.id);
  }
  //#endregion

  //#region Actions
  function openAssistant() {
    // No side panel to open (the AI is off and there is no menu photo).
    if (!hasSidePanel.value) return;
    ctx.sidePanelTab.value = 'chat';
    if (!props.wide) ctx.sheetOpen.value = true;
  }

  function setSheetOpen(open: boolean) {
    ctx.sheetOpen.value = open;
  }

  function setSheetHeight(height: TSheetHeight) {
    ctx.sheetHeight.value = height;
  }

  function setSidePanelTab(tab: TMenuSidePanelTab) {
    ctx.sidePanelTab.value = tab;
  }

  function setFilter(next: TItemFilter) {
    ctx.filter.value = next;
  }

  function setOthersExpanded(expanded: boolean) {
    othersExpanded.value = expanded;
  }

  function openQueue() {
    ctx.openItemId.value = null;
    ctx.mode.value = 'queue';
  }

  function readAgain() {
    blurActive();
    emit('source', false);
  }

  function openItem(itemId: string | null) {
    ctx.openItemId.value = itemId;
  }

  async function addItem(categoryId: string | null) {
    const order = draft.value?.categoryOrder ?? [];
    const target = categoryId ?? order[order.length - 1] ?? null;
    if (target !== null) await actions.addItem(target);
  }

  async function addCategory() {
    const categoryId = await actions.addCategory();
    if (!alive || categoryId === null) return;
    renameCategoryId.value = categoryId;
  }

  function onRenameStarted() {
    renameCategoryId.value = null;
  }

  function setAnswering(questionId: string, answering: boolean) {
    const next = new Set(answeringIds.value);
    if (answering) next.add(questionId);
    else next.delete(questionId);
    answeringIds.value = next;
  }

  /**
   * The answered card goes away and takes the focus with it: a keyboard or
   * screen-reader owner lands on the next open question, else on the
   * workspace heading, instead of the top of the page.
   */
  async function focusAfterAnswer() {
    await nextTick();
    if (!alive || !listMode.value) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const root = main.value;
    const nextCard = root?.querySelector('[data-question-id]');
    const target =
      nextCard?.querySelector('button:not([disabled])') ??
      root?.querySelector('.ws-title');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /** Saves one answer; each card waits only for its own. True once saved. */
  async function answer(
    question: TDraftQuestion,
    payload: IAnswerQuestionDto
  ): Promise<boolean> {
    if (answeringIds.value.has(question.id)) return false;
    setAnswering(question.id, true);
    const answered = await actions.answerQuestion(question, payload);
    if (!alive) return answered;
    setAnswering(question.id, false);
    if (answered) await focusAfterAnswer();
    return answered;
  }

  function hideUnpriced() {
    void actions.hideUnpriced();
  }

  // «Revisar uno por uno» (V2MenuReviewQueue).
  function queueOps(ops: TDraftOp[]) {
    void ctx.mutate(ops);
  }

  function queueConfirm(itemId: string) {
    void actions.confirmItem(itemId);
  }

  /** The queue moves on only once the answer is saved. */
  function queueAnswer(
    questionId: string,
    payload: IAnswerQuestionDto
  ): Promise<boolean> {
    const question = draft.value?.questions[questionId];
    return question ? answer(question, payload) : Promise.resolve(false);
  }

  function closeQueue() {
    ctx.mode.value = 'list';
  }

  function dismissPages(pageIds: string[]) {
    dismissedPages.value = new Set([...dismissedPages.value, ...pageIds]);
  }

  async function cancelRead() {
    const confirmed = await modals.confirm({
      title: tr('menuImport.reading.cancelTitle'),
      message: tr(readCopyKeys(read.value, pages.value).cancelMessage),
      acceptText: tr('menuImport.reading.cancelAccept'),
      cancelText: tr('menuImport.reading.cancelKeep'),
      acceptColor: 'error',
    });
    if (!confirmed || !alive || acting.value) return;
    acting.value = true;
    const cancelled = await ctx.cancelRead();
    if (!alive) return;
    acting.value = false;
    // Nothing was found: back to the pages (they stay saved).
    const current = ctx.draft.value;
    if (
      cancelled &&
      (!current ||
        (Object.keys(current.items).length === 0 &&
          current.categoryOrder.length === 0))
    ) {
      blurActive();
      emit('source', false);
    }
  }

  /** «Intentar de nuevo»: the same pages, no upload. */
  async function retryRead() {
    const failed = read.value;
    if (!failed || acting.value) return;
    acting.value = true;
    const started = await ctx.retryRead(failed.readNo);
    if (started)
      gtag.event('menu_read_started', {
        pages: failed.pagesTotal,
        readNo: ctx.read.value?.readNo ?? null,
        hasNote: false,
      });
    if (!alive) return;
    acting.value = false;
  }

  /** «Cargarlo a mano»: the empty workspace. */
  async function startManual() {
    if (acting.value) return;
    acting.value = true;
    await ctx.startManual();
    if (!alive) return;
    acting.value = false;
  }
  //#endregion

  //#region Watchers
  // An opened dish (a tap, «Publicar menú», a failed publish) or one picked in «Carta original» comes into view.
  watch(openItemId, (itemId) => {
    if (itemId !== null) void revealItem(itemId);
  });
  watch(selectedItemId, (itemId) => {
    if (itemId !== null) void revealItem(itemId);
  });

  // A dish that left the draft (removed, undone) cannot stay open.
  watch(draft, (current) => {
    const itemId = ctx.openItemId.value;
    if (itemId !== null && !current?.items[itemId]) ctx.openItemId.value = null;
  });

  // Wide screens dock the side panel; outside the review there is none.
  watch(
    () => props.wide,
    (wide) => {
      if (wide) ctx.sheetOpen.value = false;
    }
  );
  // Nothing left for the side panel (the AI went off, no menu photo): its sheet closes.
  watch(hasSidePanel, (useful) => {
    if (!useful) ctx.sheetOpen.value = false;
  });
  watch(
    phase,
    (next) => {
      if (next !== 'review') ctx.sheetOpen.value = false;
      if (next === 'reading') ctx.mode.value = 'list';
    },
    { immediate: true }
  );

  // A page asked for («Ver en la carta», a page chip): the side panel shows
  // «Carta original», in its sheet on narrow screens.
  watch(
    () => ctx.viewerPageId.value,
    (pageId) => {
      if (pageId === null) return;
      ctx.sidePanelTab.value = 'original';
      if (!props.wide && phase.value === 'review') ctx.sheetOpen.value = true;
    }
  );
  // The request is spent once the original is out of sight, so asking for the
  // same page again opens it again (the viewer keeps showing its page).
  watch(sheetOpen, (open) => {
    if (!open) ctx.viewerPageId.value = null;
  });
  watch(sidePanelTab, (tab) => {
    if (tab === 'chat') ctx.viewerPageId.value = null;
  });

  // The one-time end card; a new read starts clean.
  watch(read, (next, previous) => {
    if (isReadFinish(previous, next) && next) doneReadNo.value = next.readNo;
    if (next?.status === 'running' && previous?.readNo !== next.readNo) {
      doneReadNo.value = null;
      dismissedPages.value = new Set();
    }
  });

  function clearSlowTimer() {
    if (slowTimer !== null) clearTimeout(slowTimer);
    slowTimer = null;
  }

  // «Está tardando más de lo normal» after 90 s without the end (longer
  // for a read of many parts of very tall pages).
  watch(
    () => (read.value?.status === 'running' ? read.value.startedAt : null),
    (startedAt) => {
      clearSlowTimer();
      slow.value = false;
      if (startedAt === null) return;
      const delay = readSlowDelay(
        startedAt,
        Date.now(),
        read.value?.pagesTotal ?? 0
      );
      if (delay === 0) {
        slow.value = true;
        return;
      }
      slowTimer = setTimeout(() => {
        slowTimer = null;
        slow.value = true;
      }, delay);
    },
    { immediate: true }
  );
  //#endregion

  onMounted(() => {
    // A fresh workspace starts with its phone sheet closed.
    ctx.sheetOpen.value = false;
  });

  onBeforeUnmount(() => {
    alive = false;
    clearSlowTimer();
  });

  defineExpose({ bar, primary, secondary, back });
</script>

<template>
  <div
    class="v2-menu-workspace"
    :class="{
      'is-wide': wide,
      'has-side': wide && phase !== 'failed' && (reading || hasSidePanel),
      'has-fab': showFab,
    }"
  >
    <div ref="main" class="ws-main">
      <div class="ws-inner">
        <h1 class="ws-title" tabindex="-1">
          {{ $t('storeSetup.steps.menu') }}
        </h1>
        <V2MenuReadFailed
          v-if="phase === 'failed'"
          :reason="read ? read.reason : null"
          :ai-enabled="aiEnabled"
          :disabled="acting"
          :online="online"
          @retake="emit('source', false)"
          @type="emit('source', true)"
          @manual="startManual"
          @retry="retryRead"
        />
        <template v-else>
          <V2MenuReadProgress
            v-if="reading && read"
            :read="read"
            :pages="pages"
            :slow="slow"
            :found="found"
            :update="readingUpdate"
            @cancel="cancelRead"
          />
          <section v-else-if="showDoneCard" class="done-card" role="status">
            <PosUiIcon name="check-circle" class="done-icon" />
            <p class="done-text">{{ doneLine }}</p>
            <PosUiButton
              v-show="canReviewMenu"
              color="neutral"
              @click="reviewMenu"
              >{{ $t('menuImport.reading.reviewCta') }}</PosUiButton
            >
          </section>
          <p v-if="partialCount !== null" class="partial-line" role="status">
            <PosUiIcon name="alert-outline" class="partial-icon" />
            <span>{{
              $tc('menuImport.partial', partialCount, { count: partialCount })
            }}</span>
          </p>
          <template v-if="!reading && hasItems && summary && counts">
            <V2MenuSummary
              :summary="summary"
              :source="source"
              :incomplete="readIncomplete"
              :update="summaryUpdate"
              @queue="openQueue"
              @hide-unpriced="hideUnpriced"
              @show-others="showOthers"
            />
            <V2MenuFilterBar
              v-if="listMode"
              :value="filter"
              :counts="counts"
              :filters="filters"
              @input="setFilter"
            />
          </template>
          <template v-if="listMode">
            <div v-if="questions.length > 0" class="questions">
              <V2MenuQuestionCard
                v-for="question in visibleQuestions"
                :key="question.id"
                :question="question"
                :draft="draft"
                :currency="currency"
                :country="country"
                :busy="answeringIds.has(question.id)"
                @answer="answer(question, $event)"
              />
              <PosUiButton
                v-if="moreQuestions > 0"
                class="questions-more"
                color="neutral"
                variant="ghost"
                icon="chevron-down"
                @click="showAllQuestions = true"
                >{{
                  $tc('menuImport.questions.more', moreQuestions, {
                    count: moreQuestions,
                  })
                }}</PosUiButton
              >
            </div>
            <V2MenuDraftList
              v-if="showList && draft"
              ref="list"
              :draft="draft"
              :filter="listFilter"
              :preview="preview"
              :open-item-id="openItemId"
              :selected-item-id="selectedItemId"
              :currency="currency"
              :country="country"
              :show-allergens="summary ? summary.allergensAny : false"
              :show-review="!reading"
              :rename-category-id="renameCategoryId"
              :others-expanded="othersExpanded"
              @update:othersExpanded="setOthersExpanded"
              @open="openItem"
              @add-item="addItem"
              @add-category="addCategory"
              @rename-started="onRenameStarted"
              @filter="setFilter"
            />
            <div v-if="reading" class="reading-skeleton" aria-hidden="true">
              <PosUiSkeleton class="skeleton-row" />
              <PosUiSkeleton class="skeleton-row is-faint" />
            </div>
            <V2MenuPageFailedCard
              v-for="entry in retakes"
              :key="entry.key"
              :entry="entry"
              :disabled="acting || !online"
              @retry="retryRead"
              @retake="readAgain"
              @dismiss="dismissPages(entry.pageIds)"
            />
          </template>
          <V2MenuReviewQueue
            v-else-if="draft"
            :draft="draft"
            :pages="pages"
            :currency="currency"
            :country="country"
            :answer-question="queueAnswer"
            @ops="queueOps"
            @confirm="queueConfirm"
            @done="closeQueue"
          />
        </template>
      </div>
    </div>
    <V2MenuReadingSide v-if="wide && reading" class="ws-reading-side" />
    <!-- The side panel's one host: docked on wide screens, a bottom sheet on narrow ones. -->
    <PosUiSheet
      v-show="(!wide || phase === 'review') && hasSidePanel"
      class="ws-side"
      :class="wide ? 'as-aside' : 'as-sheet'"
      :docked="wide"
      :value="sheetOpen"
      :height="sheetHeight"
      :title="sheetTitle"
      @input="setSheetOpen"
      @update:height="setSheetHeight"
    >
      <V2MenuSidePanel
        :class="wide ? 'as-aside' : 'as-sheet'"
        :tab="sidePanelTab"
        :reading-active="reading"
        :wide="wide"
        :country="country"
        @tab="setSidePanelTab"
        @reveal="revealRow"
      />
    </PosUiSheet>
    <button
      v-show="showFab"
      type="button"
      class="ws-fab"
      @click="openAssistant"
    >
      <PosUiIcon name="chat-outline" :size="20" />
      <span>{{ $t('menuImport.chat.title') }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-workspace {
    @apply tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-canvas;

    // Wide: the menu column and the 380 px side pane, each scrolling on its own.
    &.has-side {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 380px;
      grid-template-rows: minmax(0, 1fr);
    }

    .ws-main {
      @apply tw-min-h-0 tw-flex-1 tw-overflow-y-auto;
      overscroll-behavior: contain;
    }
    .ws-inner {
      @apply tw-mx-auto tw-flex tw-w-full tw-max-w-[52rem] tw-flex-col tw-gap-4 tw-px-4 tw-py-4 md:tw-px-6;
    }
    &.is-wide .ws-inner {
      @apply tw-py-6;
    }
    // The floating «Pedir cambios» (1 rem from the bottom, 3 rem tall) never
    // covers the last row once scrolled to the end, nor what the list scrolls
    // into view or focuses: 2 rem of air above it.
    &.has-fab {
      .ws-main {
        @apply tw-scroll-pb-24;
      }
      .ws-inner {
        @apply tw-pb-24;
      }
    }
    .ws-title {
      @apply tw-sr-only;
    }

    .done-card {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;
      .done-icon {
        @apply tw-shrink-0 tw-text-2xl tw-text-emerald-600;
      }
      .done-text {
        @apply tw-m-0 tw-min-w-[12rem] tw-flex-1 tw-text-sm tw-font-medium tw-text-neutral-900 md:tw-text-base;
      }
    }
    .partial-line {
      @apply tw-m-0 tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-border tw-border-solid tw-border-amber-200 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-neutral-900;
      .partial-icon {
        @apply tw-mt-0.5 tw-shrink-0 tw-text-base tw-text-amber-700;
      }
    }
    .questions {
      @apply tw-flex tw-flex-col tw-gap-3;
      .questions-more {
        @apply tw-self-start;
      }
    }
    .reading-skeleton {
      @apply tw-flex tw-flex-col tw-gap-2;
      .skeleton-row {
        @apply tw-h-16 tw-rounded-xl;
        &.is-faint {
          @apply tw-opacity-60;
        }
      }
    }

    .ws-side.as-aside {
      @apply tw-min-h-0 tw-border-0 tw-border-l tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
    .ws-reading-side {
      @apply tw-min-h-0;
    }

    // Neutral-900, not orange: the one orange button of the view is «Publicar menú».
    .ws-fab {
      @apply tw-absolute tw-bottom-4 tw-right-4 tw-z-10 tw-flex tw-min-h-12 tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-neutral-900 tw-px-5 tw-text-sm tw-font-medium tw-text-white tw-shadow-lg hover:tw-bg-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40 tw-ring-offset-2;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-workspace .reading-skeleton .skeleton-row {
      animation: none;
    }
  }
</style>
